import { useState, useEffect } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getAdminInquiries, answerInquiry } from "@/api/adminCsApi";

// 타입 정의
interface InquiryImage { imageId: number; imageUrl: string; }
interface InquiryAnswer { content: string; createdAt: string; }
interface Inquiry {
  inquiryId: number;
  memberId: number;
  category: string;
  title: string;
  content: string;
  status: string;
  createdAt: string;
  answer: InquiryAnswer | null;
  images: InquiryImage[];
}

export default function AdminInquiryPage() {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // 모달 상태 관리
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [answerContent, setAnswerContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      setIsLoading(true);
      const res = await getAdminInquiries();
      if (res.success) {
        setInquiries(res.data);
      }
    } catch (error) {
      alert("데이터를 불러오는데 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 답변 모달 열기
  const openAnswerModal = (inquiry: Inquiry) => {
    setSelectedInquiry(inquiry);
    setAnswerContent(""); // 입력창 초기화
  };

  // 답변 모달 닫기
  const closeAnswerModal = () => {
    setSelectedInquiry(null);
    setAnswerContent("");
  };

  // 답변 등록 처리
  const handleSubmitAnswer = async () => {
    if (!selectedInquiry) return;
    if (!answerContent.trim()) {
      alert("답변 내용을 입력해주세요.");
      return;
    }

    if (confirm("답변을 등록하시겠습니까? (등록 후 수정이 불가합니다)")) {
      try {
        setIsSubmitting(true);
        await answerInquiry(selectedInquiry.inquiryId, answerContent);
        alert("답변이 성공적으로 등록되었습니다.");
        closeAnswerModal();
        fetchInquiries(); // 목록 새로고침
      } catch (error) {
        alert("답변 등록 중 오류가 발생했습니다.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const formatDate = (dateString: string) => dateString ? dateString.substring(0, 16).replace("T", " ") : "";

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold">1:1 문의 관리</h2>
          <p className="mt-2 text-sm text-slate-500">
            고객의 문의 사항을 확인하고 답변을 등록할 수 있습니다.
          </p>
        </div>

        <Card className="rounded-[28px] border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl">전체 문의 내역</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px] text-sm">
                <thead>
                <tr className="border-b border-slate-200 text-left text-slate-500">
                  <th className="px-2 py-3 font-medium">번호</th>
                  <th className="px-2 py-3 font-medium">유형</th>
                  <th className="px-2 py-3 font-medium">제목</th>
                  <th className="px-2 py-3 font-medium">작성일시</th>
                  <th className="px-2 py-3 font-medium text-center">상태</th>
                  <th className="px-2 py-3 font-medium text-center">관리</th>
                </tr>
                </thead>
                <tbody>
                {isLoading ? (
                  <tr><td colSpan={6} className="py-10 text-center text-slate-500">로딩 중...</td></tr>
                ) : inquiries.length === 0 ? (
                  <tr><td colSpan={6} className="py-10 text-center text-slate-500">등록된 문의가 없습니다.</td></tr>
                ) : (
                  inquiries.map((inq) => (
                    <tr key={inq.inquiryId} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-2 py-4 text-slate-500">{inq.inquiryId}</td>
                      <td className="px-2 py-4 font-medium text-slate-700">[{inq.category}]</td>
                      <td className="px-2 py-4 font-medium text-slate-900 truncate max-w-[200px]">{inq.title}</td>
                      <td className="px-2 py-4 text-slate-500">{formatDate(inq.createdAt)}</td>
                      <td className="px-2 py-4 text-center">
                                                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${
                                                      inq.status === 'DONE' ? 'bg-indigo-50 text-indigo-700' : 'bg-rose-50 text-rose-600'
                                                    }`}>
                                                        {inq.status === 'DONE' ? '답변완료' : '답변대기'}
                                                    </span>
                      </td>
                      <td className="px-2 py-4 text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-lg h-8"
                          onClick={() => openAnswerModal(inq)}
                        >
                          {inq.status === 'DONE' ? '상세보기' : '답변하기'}
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 💡 팝업 모달 영역 (선택된 문의가 있을 때만 렌더링) */}
      {selectedInquiry && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-[28px] bg-white p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="mb-6 flex items-center justify-between border-b border-slate-100 pb-4">
              <h2 className="text-xl font-bold text-slate-800">문의 상세 및 답변</h2>
              <button onClick={closeAnswerModal} className="text-2xl text-slate-400 hover:text-slate-700">&times;</button>
            </div>

            {/* 문의 내용 영역 */}
            <div className="space-y-4 mb-6 text-sm">
              <div className="flex gap-2">
                <span className="font-bold text-slate-700 w-16 shrink-0">유형</span>
                <span>{selectedInquiry.category}</span>
              </div>
              <div className="flex gap-2">
                <span className="font-bold text-slate-700 w-16 shrink-0">제목</span>
                <span className="font-medium">{selectedInquiry.title}</span>
              </div>
              <div className="flex gap-2 flex-col">
                <span className="font-bold text-slate-700 w-16 shrink-0">내용</span>
                <div className="bg-slate-50 p-4 rounded-xl min-h-[100px] whitespace-pre-wrap text-slate-600 leading-relaxed border border-slate-100">
                  {selectedInquiry.content}
                </div>
              </div>

              {/* 첨부 이미지 표시 */}
              {selectedInquiry.images && selectedInquiry.images.length > 0 && (
                <div className="flex gap-2 flex-col mt-4">
                  <span className="font-bold text-slate-700 w-16 shrink-0">첨부파일</span>
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {selectedInquiry.images.map((img) => (
                      <img key={img.imageId} src={img.imageUrl} alt="첨부" className="h-24 w-24 rounded-lg object-cover border border-slate-200 cursor-pointer" onClick={() => window.open(img.imageUrl)} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* 답변 영역 */}
            <div className="border-t border-slate-200 pt-6">
              <h3 className="font-bold text-slate-800 mb-3 text-sm">관리자 답변</h3>
              {selectedInquiry.status === 'DONE' && selectedInquiry.answer ? (
                <div className="bg-indigo-50/50 p-4 rounded-xl whitespace-pre-wrap text-slate-700 text-sm leading-relaxed border border-indigo-100">
                  {selectedInquiry.answer.content}
                  <div className="mt-3 text-xs text-slate-400 font-medium">
                    답변일시: {formatDate(selectedInquiry.answer.createdAt)}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                                    <textarea
                                      value={answerContent}
                                      onChange={(e) => setAnswerContent(e.target.value)}
                                      placeholder="고객에게 전송할 답변 내용을 입력해주세요."
                                      className="w-full h-40 rounded-xl border border-slate-300 p-4 text-sm focus:border-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-800 resize-none"
                                    />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={closeAnswerModal} className="rounded-xl">취소</Button>
                    <Button onClick={handleSubmitAnswer} disabled={isSubmitting} className="bg-slate-900 text-white hover:bg-slate-800 rounded-xl">
                      {isSubmitting ? "등록 중..." : "답변 등록완료"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}